import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { QuickViewModalShellComponent } from '@clinic-platform/ui-components';

import { AuthService } from '../../../core/auth/auth.service';
import { UserRole } from '../../../core/auth/user-role.type';
import { SystemAdminContextService } from '../../../core/context/system-admin-context.service';
import { resolveHttpErrorMessageKey } from '../../../core/http/http-error-message.util';
import { SettingsService } from '../../settings/data-access/settings.service';
import { ClinicDetailDto } from '../../settings/data-access/settings.models';

@Component({
  selector: 'app-clinic-quick-view-modal',
  standalone: true,
  imports: [QuickViewModalShellComponent],
  template: `
    <lib-quick-view-modal-shell
      [isOpen]="isOpen && !!clinicId"
      [title]="title"
      subtitle="Read-only clinic context without leaving the current workflow."
      [isLoading]="isLoading"
      loadingText="Loading clinic..."
      [errorText]="errorText"
      (close)="close.emit()"
    >
      @if (clinic) {
        <div class="quick-view-grid">
          <div class="quick-view-field">
            <span>ID</span>
            <strong [title]="clinic.id">{{ readableId(clinic.id) }}</strong>
            @if (showIdMeta(clinic.id)) {
              <div class="quick-view-id-meta">{{ clinic.id }}</div>
            }
          </div>

          <div class="quick-view-field">
            <span>Name</span>
            <strong>{{ clinic.name }}</strong>
          </div>

          <div class="quick-view-field">
            <span>City</span>
            <strong>{{ clinic.city || '—' }}</strong>
          </div>

          <div class="quick-view-field">
            <span>Address</span>
            <strong>{{ clinic.address || '—' }}</strong>
          </div>

          <div class="quick-view-field">
            <span>Working Day</span>
            <strong>{{ clinic.operationalSettings.workingDayStart }} - {{ clinic.operationalSettings.workingDayEnd }}</strong>
          </div>

          @if (showSlotOverride) {
            <div class="quick-view-field">
              <span>Slot Override</span>
              <strong>{{ slotOverrideLabel }}</strong>
            </div>
          }

          @if (showEffectiveSlot) {
            <div class="quick-view-field">
              <span>Effective Slot</span>
              <strong>{{ effectiveSlotLabel }}</strong>
            </div>
          }

          <div class="quick-view-field">
            <span>Timezone</span>
            <strong>{{ timezoneLabel }}</strong>
          </div>
        </div>
      }
    </lib-quick-view-modal-shell>
  `,
})
export class ClinicQuickViewModalComponent implements OnChanges {
  private readonly settingsService = inject(SettingsService);
  private readonly authService = inject(AuthService);
  private readonly systemAdminContext = inject(SystemAdminContextService);

  @Input() public isOpen = false;
  @Input() public clinicId: string | null = null;
  @Input() public companyIdOverride: string | null = null;
  @Input() public title = 'Clinic Quick View';

  @Output() public readonly close = new EventEmitter<void>();

  protected isLoading = false;
  protected errorText: string | null = null;
  protected clinic: ClinicDetailDto | null = null;

  protected get showSlotOverride(): boolean {
    return typeof this.clinic?.operationalSettings.slotDurationMinutesOverride === 'number';
  }

  protected get showEffectiveSlot(): boolean {
    return typeof this.clinic?.operationalSettings.effectiveSlotDurationMinutes === 'number';
  }

  protected get slotOverrideLabel(): string {
    const value = this.clinic?.operationalSettings.slotDurationMinutesOverride;

    if (typeof value !== 'number') {
      return '';
    }

    return value > 0 ? `${value} min` : 'Inherit';
  }

  protected get effectiveSlotLabel(): string {
    const value = this.clinic?.operationalSettings.effectiveSlotDurationMinutes;

    return typeof value === 'number' ? `${value} min` : '';
  }

  protected get timezoneLabel(): string {
    const settings = this.clinic?.operationalSettings;

    if (!settings) {
      return '';
    }

    if (!settings.timeZoneSource) {
      return settings.timeZone;
    }

    return settings.timeZoneSource === 'clinic_override'
      ? settings.timeZone
      : `Inherit (${settings.timeZoneInherited ?? settings.timeZone})`;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!this.isOpen) {
      return;
    }

    if (changes['isOpen'] || changes['clinicId'] || changes['companyIdOverride']) {
      this.load();
    }
  }

  private get currentRole(): UserRole | null {
    return (this.authService.getCurrentRole() as UserRole | null) ?? null;
  }

  private get resolvedCompanyId(): string | undefined {
    const override = this.companyIdOverride?.trim() ?? '';
    if (override) {
      return override;
    }

    if (this.currentRole === 'system_admin') {
      return this.systemAdminContext.getCompanyId()?.trim() || undefined;
    }

    return undefined;
  }

  private load(): void {
    const clinicId = this.clinicId?.trim() ?? '';
    if (!this.isOpen || !clinicId) {
      return;
    }

    this.isLoading = true;
    this.errorText = null;
    this.clinic = null;

    this.settingsService.getClinicDetail(clinicId, this.resolvedCompanyId).subscribe({
      next: (clinic) => {
        this.clinic = clinic;
        this.errorText = null;
        this.isLoading = false;
      },
      error: (error: unknown) => {
        this.errorText = resolveHttpErrorMessageKey(error, {
          fallback: 'Failed to load clinic'
        });
        this.isLoading = false;
      },
    });
  }

  protected readableId(value: string): string {
    const normalized = value.trim();
    if (normalized.length <= 18) {
      return normalized;
    }

    return `${normalized.slice(0, 8)}...${normalized.slice(-6)}`;
  }

  protected showIdMeta(value: string): boolean {
    return this.readableId(value) !== value;
  }
}
